import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, X, Loader2, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const UserDeleteConfirmModal = ({ user, isOpen, onClose, onConfirm, loading = false }) => {
  const { t } = useTranslation('admin');
  const [confirmText, setConfirmText] = useState('');

  const expectedName = user?.name || user?.email || '';

  useEffect(() => {
    if (isOpen) {
      setConfirmText('');
    }
  }, [isOpen, user?.id]);

  const isConfirmed = confirmText.trim() === expectedName.trim() && expectedName.trim().length > 0;

  const handleConfirm = () => {
    if (!isConfirmed || loading) return;
    onConfirm?.();
  };

  return (
    <AnimatePresence>
      {isOpen && user && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100000]"
          />
          <motion.div
            className="fixed inset-0 flex items-center justify-center z-[100001] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 8 }}
              className="w-full max-w-md bg-[#0d1424] rounded-2xl border border-red-500/20 shadow-[0_32px_80px_rgba(0,0,0,0.7)] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between p-6 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/25 flex items-center justify-center flex-shrink-0">
                    <Trash2 className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-white">{t('userDeleteConfirmModal.title')}</h3>
                    <p className="text-xs text-[#7a96b0] mt-0.5">{t('userDeleteConfirmModal.irreversible')}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="p-1.5 rounded-lg text-[#4a6380] hover:text-white hover:bg-white/[0.06] transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="px-6 pb-6 space-y-4">
                <div className="flex items-start gap-2.5 p-3 rounded-xl bg-red-500/5 border border-red-500/15">
                  <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[#c8d8eb] leading-relaxed">
                    {t('userDeleteConfirmModal.warningPrefix')} <span className="font-medium text-white">{expectedName}</span>{t('userDeleteConfirmModal.warningSuffix')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm text-[#8aaccc] mb-2">
                    {t('userDeleteConfirmModal.confirmLabel')} <span className="text-white font-medium">{expectedName}</span>
                  </label>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={expectedName}
                    className="w-full px-4 py-2.5 bg-[#161f31] border border-white/[0.08] rounded-xl text-white placeholder-[#4d6a8a] focus:outline-none focus:ring-2 focus:ring-red-500/30"
                    autoComplete="off"
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className="px-4 py-2 rounded-xl text-sm font-medium text-[#7a96b0] hover:text-white hover:bg-white/[0.05] transition-colors"
                  >
                    {t('userDeleteConfirmModal.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={!isConfirmed || loading}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-red-500/90 hover:bg-red-500 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    <span>{t('userDeleteConfirmModal.deleteUser')}</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default UserDeleteConfirmModal;
